import { makeElementClassNameFactory, makeRootClassName } from '@shared/utils';
import clsx from 'clsx';
import type { ReactNode } from 'react';
import { FieldValues, Path, useFormContext } from 'react-hook-form';
import { FlowIcon } from '../flow-icon/FlowIcon';
import { TextField } from '../react-hook-form';
import { Text } from '../text';

const ROOT = makeRootClassName('InlineTextField');
const el = makeElementClassNameFactory(ROOT);

type InlineTextFieldProps<T> = {
  className?: string;
  fieldWrapperClassName?: string;
  isDisabled?: boolean;
  label?: string;
  labelAccessory?: ReactNode;
  name: Path<T>;
  save: (p: Path<T>) => void;
  required: boolean;
  type?: 'text' | 'number';
  placeholder?: string;
  transparentWithoutFocus?: boolean;
};

export const InlineTextField = <T extends FieldValues>({
  className,
  fieldWrapperClassName,
  isDisabled,
  name,
  label,
  labelAccessory,
  save,
  required,
  type = 'text',
  placeholder,
  transparentWithoutFocus,
}: InlineTextFieldProps<T>) => {
  const { watch, control } = useFormContext<T>();
  const value: string = watch(name);

  const onSave = () => {
    save(name);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      onSave();
    }
  };

  const selectPlaceholder = (
    placeholder: string | undefined,
    label: string | undefined
  ) => {
    if (placeholder) {
      return placeholder;
    }
    if (label) {
      return `Add ${label.toLowerCase()}`;
    }
    return '';
  };
  const selectedPlaceholder = selectPlaceholder(placeholder, label);

  return (
    <div className={clsx(ROOT, className)}>
      <div className={clsx(el`table`, label && 'with-label')}>
        {label && (
          <div
            className={clsx(
              el`label`,
              Boolean(labelAccessory) && 'flex items-center gap-1'
            )}
          >
            <Text type="body-sm">{label}</Text>
            {labelAccessory}
          </div>
        )}
        <div className={el`field-and-icon-wrapper`}>
          <TextField
            isDisabled={isDisabled}
            aria-label={label}
            onKeyDown={handleKeyDown}
            control={control}
            name={name}
            onBlur={onSave}
            size="medium"
            fieldWrapperClassName={clsx(
              el`input-wrapper`,
              value ? 'VALID' : 'INVALID',
              required && 'REQUIRED',
              fieldWrapperClassName
            )}
            transparentWithoutFocus={transparentWithoutFocus ?? true}
            placeholderClassName={clsx(
              el`placeholder`,
              value ? 'VALID' : 'INVALID',
              required && 'REQUIRED'
            )}
            placeholder={selectedPlaceholder}
            className={el`field-wrapper`}
            type={type}
          />
          <FlowIcon fieldPath={name} />
        </div>
      </div>
    </div>
  );
};
